import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Grid,
  Card,
  IconButton,
  Chip,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import Search from "@mui/icons-material/Search";
import PostMonthlyTarget from "./monthlyTarget";
import { getAllPostSeasonActivitiesAction, getAllPostMonthlyTargetsAction, getAllDistrictsAction, getAllMandalsAction } from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

function SOorMDOPostActivity() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [openMonthlyTarget, setOpenMonthlyTarget] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;

  // Fetch post-season activities and monthly targets
  useEffect(() => {
    dispatch(getAllPostSeasonActivitiesAction());
    dispatch(getAllPostMonthlyTargetsAction());
  }, [dispatch]);

  // Get districts and mandals from Redux
  const { successData: districtsData } = useSelector((state) => state.getAllDistrictsReducer || {});
  const { successData: mandalsData } = useSelector((state) => state.getAllMandalsReducer || {});
  const districtList = districtsData?.data?.districts || [];
  const mandalList = mandalsData?.data?.mandals || [];

  useEffect(() => {
    dispatch(getAllDistrictsAction());
    dispatch(getAllMandalsAction());
  }, [dispatch]);

  // Redux selectors
  const { successData: postSeasonData, loading: postSeasonLoading } = useSelector(
    (state) => state.getAllPostSeasonActivitiesReducer || {}
  );
  const { successData: allMonthlyTargetsData, loading: monthlyTargetsLoading } = useSelector(
    (state) => state.getAllPostMonthlyTargetsReducer || {}
  );

  // Build a set of post_target_ids that already have monthly targets
  const postIdsWithMonthlyTargets = useMemo(() => {
    const list =
      allMonthlyTargetsData?.data?.monthlyTargets ||
      allMonthlyTargetsData?.data ||
      allMonthlyTargetsData ||
      [];
    const arr = Array.isArray(list) ? list : [];
    return new Set(
      arr
        .map((mt) => mt.post_target_id ?? mt.postTargetId ?? mt.post_target?.id)
        .filter((id) => id !== undefined && id !== null)
        .map((id) => String(id))
    );
  }, [allMonthlyTargetsData]);

  // Normalization helper for post-season targets (with done counts)
  const normalizePostSeasonTargets = (rawData) => {
    if (!Array.isArray(rawData)) return [];
    const targets = [];
    const seenIds = new Set();
    rawData.forEach(item => {
      if (Array.isArray(item?.targets)) {
        item.targets.forEach(tgt => {
          // District-level target
          const id = tgt.id;
          if (!seenIds.has(id)) {
            seenIds.add(id);
            targets.push({
              id,
              season: tgt.season,
              target_area_type: tgt.target_area_type,
              target_area_id: tgt.target_area_id,
              dv_count: tgt.dv_count,
              dv_done_count: tgt.dv_done_count,
              fv_count: tgt.fv_count,
              fv_done_count: tgt.fv_done_count,
              fd_count: tgt.fd_count,
              fd_done_count: tgt.fd_done_count,
              is_active: tgt.is_active,
              farmers_attended: tgt.farmers_attended,
              details: Array.isArray(tgt.details) ? tgt.details : [],
            });
          }
          // Mandal-level targets
          if (Array.isArray(tgt?.mandals)) {
            tgt.mandals.forEach(mandal => {
              if (Array.isArray(mandal?.post_targets)) {
                mandal.post_targets.forEach(post => {
                  const postId = post.id;
                  if (!seenIds.has(postId)) {
                    seenIds.add(postId);
                    targets.push({
                      id: postId,
                      season: post.season,
                      target_area_type: post.target_area_type,
                      target_area_id: post.target_area_id,
                      dv_count: post.dv_count,
                      dv_done_count: post.dv_done_count,
                      fv_count: post.fv_count,
                      fv_done_count: post.fv_done_count,
                      fd_count: post.fd_count,
                      fd_done_count: post.fd_done_count,
                      is_active: post.is_active,
                      farmers_attended: post.farmers_attended,
                      details: Array.isArray(post.details) ? post.details : [],
                    });
                  }
                });
              }
            });
          }
        });
      }
    });
    return targets;
  };

  // Normalized post-season list
  const postSeasonList = useMemo(() => normalizePostSeasonTargets(postSeasonData?.data), [postSeasonData]);

  // Filter monthly targets for a post-season activity (from normalized details)
  const getMonthlyTargetsForPost = (postId) => {
    const target = postSeasonList.find(t => t.id === postId);
    return target && Array.isArray(target.details) ? target.details : [];
  };


  // Filtered post-season activities by search
  const filteredData = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return postSeasonList;
    return postSeasonList.filter((item) =>
      [item.id, item.season?.name, item.target_area_type, item.dv_count, item.fd_count, item.fv_count]
        .some((v) => String(v ?? "").toLowerCase().includes(q))
    );
  }, [query, postSeasonList]);

  // Reset to page 1 when search changes
  useEffect(() => { setPage(1); }, [query]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));
  const pagedData = filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const gridColsDesktop = "2.4fr 1.2fr 2.0fr 1.5fr 1.5fr 1.5fr 1.0fr 0.8fr 0.8fr";

 
  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 0 }}>
        Post Activity
      </Typography>

      <Card sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item size={{ xs: 12, sm: 8, md: 6 }}>
            <TextField
              size="small"
              placeholder="Search by zone name, ID, or manager..."
              slotProps={{
                input: {
                  startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
                },
              }}
              fullWidth
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 4, md: 6 }} sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" }, gap: 1 }}>
              {/* <Button
                variant="contained"
                sx={{ borderRadius: 2, mr: 1, backgroundColor: '#4F46E5', color: '#fff', }}
                onClick={() => navigate("/SOorMDO/createPostActivity")}
              >
                Add Activity
              </Button> */}
              {/* <Button
                variant="contained"
                sx={{ borderRadius: 2, backgroundColor: '#4F46E5', color: '#fff', }}
                onClick={() => setOpenMonthlyTarget(true)}
              >
                Update Monthly Target
              </Button> */}
          </Grid>
        </Grid>
      </Card>

      <Box sx={{ mt: 3 }}>
        {postSeasonLoading || monthlyTargetsLoading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box display="flex" flexDirection="column" gap={1}>
            {/* Desktop header row */}
            <Box
              elevation={0}
              sx={{
                display: { xs: 'none', sm: 'grid' },
                gridTemplateColumns: { sm: gridColsDesktop },
                alignItems: 'center',
                px: 2,
                py: 2,
                columnGap: 0,
              }}
            >
              {['Season', 'Area Type', 'Area Name', 'Dealer Visit', 'Field Day', 'Field Visit', 'Is Active', 'Actions', ].map((h) => (
                <Typography
                  key={h}
                  variant="caption"
                  fontWeight={600}
                  sx={{ textTransform: 'uppercase', textAlign: h === 'Actions' ? 'right' : 'left' }}
                >
                  {h}
                </Typography>
              ))}
            </Box>
            {/* Desktop value rows */}
            {pagedData?.map((row) => {
              const monthlyTargets = getMonthlyTargetsForPost(row.id);
              const isExpanded = expandedId === row.id;
              return (
                <React.Fragment key={row.id}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      display: { xs: 'none', sm: 'grid' },
                      gridTemplateColumns: { sm: gridColsDesktop },
                      alignItems: 'center',
                      border: '1px solid #E5E7EB',
                      px: 1.2,
                      py: 1,
                      gap: 0.5,
                      columnGap: 0.5,
                      '&:hover': {
                        border: '2px solid #2563EB',
                        boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                      },
                    }}
                  >
                    <Typography variant="body2">{row?.season?.name} {row?.season?.year}</Typography>
                    <Typography variant="body2">{row.target_area_type}</Typography>
                    <Typography variant="body2">
                      {row.target_area_type === 'mandal' ? (() => {
                        const mandal = mandalList.find(m => m.mandalId === row.target_area_id || m.id === row.target_area_id);
                        const district = districtList.find(d => d.districtId === mandal?.districtId || d.id === mandal?.districtId);
                        return district && mandal ? `${district.name} - ${mandal.name}` : mandal ? mandal.name : row.target_area_id;
                      })() : row.target_area_type === 'district' ? (() => {
                        const district = districtList.find(d => d.districtId === row.target_area_id || d.id === row.target_area_id);
                        return district ? district.name : row.target_area_id;
                      })() : row.target_area_id}
                    </Typography>
                    <Typography variant="body2">{row.dv_done_count ?? 0}/{row.dv_count ?? 0}</Typography>
                    <Typography variant="body2">{row.fd_done_count ?? 0}/{row.fd_count ?? 0}</Typography>
                    <Typography variant="body2">{row.fv_done_count ?? 0}/{row.fv_count ?? 0}</Typography>
                    <Chip
                      label={row.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        width: 90,
                        backgroundColor: row.is_active ? '#D1FAE5' : '#FEE2E2',
                        ml: -2,
                        height: 24,
                        '& .MuiChip-label': { fontSize: 12, fontWeight: 600 },
                      }}
                    />
                    <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                      {!postIdsWithMonthlyTargets.has(String(row.id)) ? (
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ borderRadius: 2, backgroundColor: '#4F46E5', color: '#fff', fontSize: 12 }}
                          onClick={() => { setSelectedPostId(row.id); setOpenMonthlyTarget(true); }}
                        >
                          Update Monthly Target
                        </Button>
                      ) : (
                        <IconButton size="small" aria-label="view" onClick={() => navigate('/SOorMDO/viewPostActivity', { state: { postActivityData: row } })}>
                          <Visibility fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                        </IconButton>
                      )}
                    </Box>
                  </Paper>
                  {/* Mobile layout wrapped in Paper */}
                  <Paper
                    elevation={0}
                    sx={{
                      display: { xs: 'block', sm: 'none' },
                      borderRadius: 3,
                      border: '1px solid #E5E7EB',
                      px: 2,
                      py: 2,
                      mb: 1.5,
                    }}
                  >
                    <Box mb={1} display="flex" alignItems="center" >
                      <Typography fontWeight={600} minWidth={100}>Season:</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <Typography variant="body2">{row?.season?.name} {row?.season?.year}</Typography>
                        <Chip
                          label={row.is_active ? "Active" : "Inactive"}
                          size="small"
                          sx={{ width: 80, backgroundColor: row.is_active ? '#10B981' : '#F87171', color: '#fff', ml: 'auto' }}
                        />
                      </Box>
                    </Box>
                    <Box mb={1} display="flex" alignItems="center" gap={0}>
                      <Typography fontWeight={600} minWidth={100}>Area Type:</Typography>
                      <Typography variant="body2">{row.target_area_type}</Typography>
                    </Box>
                    <Box mb={1} display="flex" alignItems="center" gap={0}>
                      <Typography fontWeight={600} minWidth={100}>Area Name:</Typography>
                      <Typography variant="body2">
                        {row.target_area_type === 'mandal' ? (() => {
                          const mandal = mandalList.find(m => m.mandalId === row.target_area_id || m.id === row.target_area_id);
                          const district = districtList.find(d => d.districtId === mandal?.districtId || d.id === mandal?.districtId);
                          return district && mandal ? `${district.name} - ${mandal.name}` : mandal ? mandal.name : row.target_area_id;
                        })() : row.target_area_type === 'district' ? (() => {
                          const district = districtList.find(d => d.districtId === row.target_area_id || d.id === row.target_area_id);
                          return district ? district.name : row.target_area_id;
                        })() : row.target_area_id}
                      </Typography>
                    </Box>
                    <Box mb={1} display="flex" alignItems="center" gap={0}>
                      <Typography fontWeight={600} minWidth={100}>Dealer Visit:</Typography>
                      <Box>
                        <Typography variant="body2">{row.dv_done_count ?? 0}/{row.dv_count ?? 0}</Typography>
                      </Box>
                    </Box>
                    <Box mb={1} display="flex" alignItems="center" gap={0}>
                      <Typography fontWeight={600} minWidth={100}>Field Day:</Typography>
                      <Box>
                        <Typography variant="body2">{row.fd_done_count ?? 0}/{row.fd_count ?? 0}</Typography>
                      </Box>
                    </Box>
                    <Box mb={1} display="flex" alignItems="center" gap={0}>
                      <Typography fontWeight={600} minWidth={100}>Field Visit:</Typography>
                      <Box>
                        <Typography variant="body2">{row.fv_done_count ?? 0}/{row.fv_count ?? 0}</Typography>
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0}>
                      <Typography fontSize={11} color="#000000" minWidth={100}></Typography>
                      <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                        {!postIdsWithMonthlyTargets.has(String(row.id)) ? (
                          <Button
                            variant="contained"
                            size="small"
                            sx={{ borderRadius: 2, backgroundColor: '#4F46E5', color: '#fff', fontSize: 12 }}
                            onClick={() => { setSelectedPostId(row.id); setOpenMonthlyTarget(true); }}
                          >
                            Update Monthly Target
                          </Button>
                        ) : (
                          <IconButton size="small" onClick={() => navigate('/SOorMDO/viewPostActivity', { state: { postActivityData: row } })}>
                            <Visibility fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                  {/* Collapsible Monthly Targets */}
                  {isExpanded && monthlyTargets.length > 0 && (
                    <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, mt: -1, mb: 2, mx: 2, p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#4B5563" }}>
                        Monthly Targets
                      </Typography>
                      <Table size="small">
                        <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Month ID</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Month</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Dealer Visit</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Field Visit</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Field Day</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {monthlyTargets.map((target) => (
                            <TableRow key={target.id}>
                              <TableCell>{target.id}</TableCell>
                              <TableCell>{target.month_year || target.monthYear}</TableCell>
                              <TableCell>{target.dv_done_count ?? 0}/{target.dv_count ?? 0}</TableCell>
                              <TableCell>{target.fv_done_count ?? 0}/{target.fv_count ?? 0}</TableCell>
                              <TableCell>{target.fd_done_count ?? 0}/{target.fd_count ?? 0}</TableCell>
                              <TableCell>
                                <Chip
                                  label={target.is_active ? "Active" : "Inactive"}
                                  size="small"
                                  sx={{
                                    backgroundColor: target.is_active ? '#D1FAE5' : '#FECACA',
                                    color: target.is_active ? '#047857' : '#B91C1C',
                                    fontWeight: 600,
                                    fontSize: 11
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  )}
                </React.Fragment>
              );
            })}
            {filteredData.length === 0 && (
              <Typography align="center" color="text.secondary" sx={{ mt: 2 }}>
                No post-season activities found.
              </Typography>
            )}
            {totalPages > 1 && (
              <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gap={1}>
                <Typography variant="body2" color="text.secondary">
                  Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredData.length)} to {Math.min(page * PAGE_SIZE, filteredData.length)} of {filteredData.length} entries
                </Typography>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, val) => setPage(val)}
                  color="primary"
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
      <PostMonthlyTarget
        open={openMonthlyTarget}
        onClose={() => { setOpenMonthlyTarget(false); setSelectedPostId(null); }}
        onSubmit={() => { dispatch(getAllPostSeasonActivitiesAction()); dispatch(getAllPostMonthlyTargetsAction()); }}
        defaultPostTargetId={selectedPostId}
      />
    </Box>
   
  );
}

export default SOorMDOPostActivity;